<template>
    <b-tabs class="tabs-pills" v-model="tabComputed">
        <b-tab :title="'General|Général' | tr" class="py-3 px-1">
            <h4 class="mb-4" v-tr>Page Settings|Paramètres de la page</h4>

            <e-translation-input label="Page Name|Nom de la page" v-model="currentPage.name" auto-translate
                :useLocales="cmsLocales">
                <template #form-help>
                    <div class="form-help mt-2" v-tr>
                        The page name will be used for navigation links|
                        Le nom de la page sera utilisé pour les liens de navigation
                    </div>
                </template>
            </e-translation-input>

            <b-form-group :label="'Page Icon|Icône de la page' | tr">
                <input-glyph v-model="currentPage.glyph" />
            </b-form-group>

            <hr>

            <b-form-group>
                <b-checkbox v-model="currentPage.homePage">
                    <span v-tr>Home Page|Page d'accueil</span>
                </b-checkbox>
            </b-form-group>

            <e-translation-input v-if="!currentPage.homePage" label="Permalink|Permalien"
                v-model="currentPage.permalink" auto-translate :use-locales="cmsLocales">
                <template #label-info>
                    <e-hint text="Important field for the SEO. The permalink is the text that will display in the URL: max 5 words, no special characters, no spaces, only '-' to separate words|Valeur importante pour le SEO. Le permalien est le texte qui s'affichera dans l'URL : maximum 5 mots, pas de caractères spéciaux, pas d'espaces, seulement '-' pour séparer les mots" />
                </template>
                <template #form-help>
                    <div class="form-help mt-2">https://{{ cmsWebsite.domain }}/{{ tr(currentPage.permalink) }}</div>
                    <div class="form-help mt-2" v-tr>
                        No Space and No Special Characters|Aucun espace ni caractères spéciaux
                    </div>
                </template>
                <template #append>
                    <b-btn variant="light" @click="updatePermalink">
                        <i class="fas fa-sync" />
                    </b-btn>
                </template>
            </e-translation-input>

            <b-form-group v-if="canAdminEyeInWireless()" :label="tr(`Page Type|Type de page`)">
                <b-select v-model="currentPage.type" @change="onCurrentpageTypeChange">
                    <option value="blocks" v-tr>Regular Page|Page normale</option>
                    <option value="landing-page" v-tr>Landing Page|Page marketing (landing)</option>
                    <option value="cms-item-list" v-tr>CMS Items List|Liste d'éléments du CMS</option>
                    <option value="cms-item-view" v-tr>CMS Item Details|Page d'un élément du CMS</option>
                    <option value="landing-page" v-tr>Landing Page|Page marketing (landing)</option>
                    <option value="map" v-tr>Wayfinder|Wayfinding</option>
                    <option value="template" v-tr>Template|Modèle</option>
                    <option value="test" v-tr>Test</option>
                    <!--                            <option value="search" v-tr>Search Page|Page de recherche</option>-->
                    <option value="universal-search" v-tr>Site Search|Rechercher dans le site</option>
                    <option value="blog">Blog</option>
                </b-select>
            </b-form-group>

            <b-form-group>
                <b-checkbox v-model="currentPage.template" disabled>
                    <span v-tr>Template Only|Gabarit seulement</span>
                </b-checkbox>
            </b-form-group>

            <hr>

            <b-form-group :label="'Page Status|Statut de la page' | tr">
                <b-select variant="light" v-model="currentPage.status">
                    <b-select-option value="draft">
                        <i class="fas fa-sheet-plastic" />
                        <span v-tr>Draft|Brouillon</span>
                    </b-select-option>
                    <b-select-option value="pending">
                        <i class="fas fa-clock" />
                        <span v-tr>Pending Review|En attente</span>
                    </b-select-option>
                    <b-select-option value="published">
                        <i class="fas fa-circle-check" />
                        <span v-tr>Published|Publié</span>
                    </b-select-option>
                    <b-select-option value="archive">
                        <i class="fas fa-box-archive" />
                        <span v-tr>Archive|Archivé</span>
                    </b-select-option>
                </b-select>
                <div class="form-help mt-2" v-tr>
                    This page will be visible only if the status is set to published|
                    Cette page sera visible uniquement si son statut est publié
                </div>
            </b-form-group>

            <b-form-group :label="'Category|Catégorie' | tr">
                <b-input-group>
                    <b-select v-model="currentPage.category" :disabled="categoriesLoading">
                        <b-select-option :value="''" v-tr>No Category|Aucune catégorie</b-select-option>
                        <b-select-option v-for="cat in categories" :key="`cat-${cat.id || cat._id || cat.key}`"
                            :value="cat.key">
                            {{ cat.name | tr }}
                        </b-select-option>
                    </b-select>
                    <b-input-group-append>
                        <b-btn variant="light" @click="openCategoryModal"
                            :title="'Create Category|Créer une catégorie' | tr" v-b-tooltip>
                            <i class="fas fa-plus" />
                        </b-btn>
                    </b-input-group-append>
                </b-input-group>
                <div class="form-help mt-2" v-tr>
                    Organize your pages with categories.|Organisez vos pages avec des catégories.
                </div>
            </b-form-group>

            <!--            <b-form-group>-->
            <!--                <b-checkbox v-model="currentPage.navigationBehavior" value="visible" unchecked-value="hidden">-->
            <!--                    <span v-tr>Show in Navigation|Montrer dans la navigation</span>-->
            <!--                </b-checkbox>-->
            <!--            </b-form-group>-->
        </b-tab>
        <b-tab v-if="currentPage.type === 'blog'" title="Blog" class="py-3 px-1">
            <h4 class="mb-4" v-tr>Blog Settings|Paramètres du blog</h4>

            <b-form-group label-cols-lg="3" :label="'Original Article Link|Lien de l\'article original' | tr">
                <b-input type="url" v-model="currentPage.blogSettings.originalArticleUrl" :placeholder="'Republish URL of an existing article|URL de l\'article original' | tr" />
            </b-form-group>

            <b-form-group label-cols-lg="3" :label="'Keywords|Mots-clés' | tr">
                <b-tags v-model="currentPage.blogSettings.keywords" />
            </b-form-group>

            <b-form-group label-cols-lg="3" :label="'Author|Auteur' | tr">
                    <b-input v-model="currentPage.blogSettings.author" :placeholder="'Author Name|Nom de l\'auteur' | tr" />
            </b-form-group>
        </b-tab>

        <b-tab :title="'AI Assistant' | tr" class="py-3 px-1">
            <h4 class="mb-4" v-tr>Step 1 - Page Info|Étape 1</h4>

            <b-form-group :label="tr(`What is this page talking about/Search intention on Google|De quoi parle la page/intention de recherche sur Google`)">
                <e-translation-input v-model="currentPage.hiddenMeta.topic"
                    placeholder="Search Intent|Intention de recherche" type="textarea" required :allow-empty="false"
                    auto-translate :use-locales="cmsLocales" />
            </b-form-group>

            <sidebar-group title="Target|Cible">
                <b-form-group :label="tr(`Audience (optional)|Audience (optionnel)`)">
                    <e-translation-input v-model="currentPage.hiddenMeta.audience" placeholder="Ex: B2B IT Departments"
                        auto-translate :use-locales="cmsLocales" />
                </b-form-group>
                <b-form-group :label="tr(`Target Market (optional)|Marché ciblé (optionnel)`)">
                    <e-translation-input v-model="currentPage.hiddenMeta.targetMarket" placeholder="Ex: Shopping mall"
                        auto-translate :use-locales="cmsLocales" />
                </b-form-group>
                <b-form-group :label="tr(`Country/State/City Targeted (optional)|Pays/Province/Ville ciblée`)">
                    <e-translation-input v-model="currentPage.hiddenMeta.targetLocation"
                        placeholder="Ex: USA|Québec|Miami" auto-translate :use-locales="cmsLocales" />
                </b-form-group>
            </sidebar-group>

            <b-btn @click="generateAIPageSpecs" size="sm" variant="success" class="float-right mt-2">
                <i class="fas fa-sync mr-1" /> <span v-tr>Sync AI Specs</span>
            </b-btn>
            <h4 class="mb-4" v-tr>Step 2 - Page Specifications|Étape 2 - Spécifications de la page</h4>

            <sidebar-group title="Page Specifications|Structure de la page">
                <template #append>
                    <div class="clear-both" />
                </template>
                <div style="max-width: 450px;">
                    <b-form-group label-cols-lg="3" :label="tr(`Words|Nombre de mots`)">
                        <b-input-group>
                            <b-input-group-text>Min</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.words[displayLanguage].min" :min="1" />
                            <b-input-group-text>Max</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.words[displayLanguage].max" :min="1" />
                        </b-input-group>
                    </b-form-group>
                    <b-form-group label-cols-lg="3" :label="tr(`Titles|Nombre de titres`)">
                        <b-input-group>
                            <b-input-group-text>Min</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.titles[displayLanguage].min" :min="1" />
                            <b-input-group-text>Max</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.titles[displayLanguage].max" :min="1" />
                        </b-input-group>
                    </b-form-group>
                    <b-form-group label-cols-lg="3" :label="tr(`Paragraphs|Nombre de paragraphes`)">
                        <b-input-group>
                            <b-input-group-text>Min</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.paragraphs[displayLanguage].min"
                                :min="1" />
                            <b-input-group-text>Max</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.paragraphs[displayLanguage].max"
                                :min="1" />
                        </b-input-group>
                    </b-form-group>
                    <b-form-group label-cols-lg="3" :label="tr(`Images|Nombre d'images`)">
                        <b-input-group>
                            <b-input-group-text>Min</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.images[displayLanguage].min" :min="1" />
                            <b-input-group-text>Max</b-input-group-text>
                            <e-input type="number" v-model="currentPage.specs.images[displayLanguage].max" :min="1" />
                        </b-input-group>
                    </b-form-group>
                </div>
            </sidebar-group>

            <b-btn @click="generateAIKeywords" size="sm" variant="success" class="float-right mt-2">
                <i class="fas fa-sync mr-1" /> <span v-tr>Sync AI Keywords</span>
            </b-btn>

            <h4 class="mb-4" v-tr>Step 3 - Keywords|Étape 3 - Mots-clés</h4>

            <sidebar-group title="Keywords|Mots-clés">
                <template #append>
                    <div class="clear-both" />
                </template>

                <b-form-group :label="tr(`Main Keywords (max 2)|Mots-clés principaux (max 2)`)">
                    <!-- TODO replace with e-translate-tags later -->
                    <b-input-group>
                        <b-input-group-text>
                            <b-img :src="getPublicIcon('flags/united-states')" style="max-width: 20px;" />
                        </b-input-group-text>
                        <b-tags v-model="currentPage.hiddenMeta.mainKeywords.en" />
                        <b-dropdown variant="light">
                            <b-dropdown-item @click="currentPage.hiddenMeta.mainKeywords.en = []">
                                <i class="fas fa-trash" />
                                <span v-tr>Clear|Vider</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-input-group>
                </b-form-group>

                <b-form-group>
                    <b-input-group>
                        <b-input-group-text>
                            <b-img :src="getPublicIcon('flags/france')" style="max-width: 20px;" />
                        </b-input-group-text>
                        <b-tags v-model="currentPage.hiddenMeta.mainKeywords.fr" />
                        <b-dropdown variant="light">
                            <b-dropdown-item @click="currentPage.hiddenMeta.mainKeywords.fr = []">
                                <i class="fas fa-trash" />
                                <span v-tr>Clear|Vider</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-input-group>
                </b-form-group>

                <hr>

                <b-form-group :label="tr(`Other Keywords|Autres mots-clés`)">
                    <!-- TODO replace with e-translate-tags later -->
                    <b-input-group>
                        <b-input-group-text>
                            <b-img :src="getPublicIcon('flags/united-states')" style="max-width: 20px;" />
                        </b-input-group-text>
                        <b-tags v-model="currentPage.hiddenMeta.keywords.en" />
                        <b-dropdown variant="light">
                            <b-dropdown-item @click="currentPage.hiddenMeta.keywords.en = []">
                                <i class="fas fa-trash" />
                                <span v-tr>Clear|Vider</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-input-group>
                </b-form-group>

                <b-form-group>
                    <b-input-group>
                        <b-input-group-text>
                            <b-img :src="getPublicIcon('flags/france')" style="max-width: 20px;" />
                        </b-input-group-text>
                        <b-tags v-model="currentPage.hiddenMeta.keywords.fr" />
                        <b-dropdown variant="light">
                            <b-dropdown-item @click="currentPage.hiddenMeta.keywords.fr = []">
                                <i class="fas fa-trash" />
                                <span v-tr>Clear|Vider</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-input-group>
                </b-form-group>
            </sidebar-group>

            <sidebar-group v-if="cmsPage.specs.mainKeywords[displayLanguage]"
                title="Keywords Specs|Spécifications des mots-clés">
                <template #actions>
                    <b-btn @click="showVariations = !showVariations" class="float-right" size="sm" variant="light"><i
                            class="fas" :class="showVariations ? `fa-minus` : `fa-plus`" /></b-btn>
                </template>
                <b-form-group :label="tr(`Main Keywords|Mots-clés principaux`)">
                    <input-keyword-specs v-for="(keyword, index) in cmsPage.specs.mainKeywords[displayLanguage]"
                        :key="`mk-${index}`" :value="keyword"
                        @remove="cmsPage.specs.mainKeywords[displayLanguage].splice(index, 1)"
                        :show-variations="showVariations" />
                </b-form-group>
                <b-form-group>
                    <template #label>
                        <span v-tr>Other Keywords|Autres mots-clés</span>
                        ({{ cmsPage.specs.keywords[displayLanguage]?.length }})
                    </template>
                    <template v-if="showVariations">
                        {{ cmsPage.getKeywordStats(displayLanguage) }}
                    </template>

                    <input-keyword-specs v-for="(keyword, index) in cmsPage.specs.keywords[displayLanguage]"
                        :key="`ok-${index}`" :value="keyword"
                        @remove="cmsPage.specs.keywords[displayLanguage].splice(index, 1)"
                        :show-variations="showVariations" />
                </b-form-group>
                <div class="form-help mb-2">If a keyword doesn't show here or should not appear anymore, please sync
                    Keywords again</div>
            </sidebar-group>

            <b-btn @click="generateAIPageMeta" size="sm" variant="success" class="float-right mt-2">
                <i class="fas fa-sync mr-1" /> <span v-tr>Sync AI Meta</span>
            </b-btn>
            <h4 class="mb-4" v-tr>Step 4 - Page Meta|Étape 4 - Metadonnées</h4>

            <sidebar-group v-if="currentPage.ai.meta[displayLanguage]" title="Page Meta Suggestions">
                <b-form-group label="Page Title">
                    <b-input-group>
                        <div class="form-control h-auto">
                            {{ currentPage.ai.meta[displayLanguage].title }}
                        </div>
                        <b-btn variant="light" @click="applySeoTitle">Apply</b-btn>
                    </b-input-group>
                </b-form-group>
                <b-form-group label="Page Description">
                    <b-input-group>
                        <div class="form-control h-auto">
                            {{ currentPage.ai.meta[displayLanguage].description }}
                        </div>
                        <b-btn variant="light" @click="applySeoDescription">Apply</b-btn>
                    </b-input-group>
                </b-form-group>
            </sidebar-group>

            <!-- Add competitors pages here -->

            <sidebar-group title="Competitors|Compétiteurs">
                <b-list-group v-if="currentPage.hiddenMeta.competitors[displayLanguage]">
                    <b-list-group-item v-for="(url, index) in currentPage.hiddenMeta.competitors[displayLanguage]"
                        class="d-flex align-items-center interactive p-1" :key="`script-${index}`" :title="url">
                        <b-input-group>
                            <e-input type="url" v-model="currentPage.hiddenMeta.competitors[displayLanguage][index]" />
                        </b-input-group>
                        <e-button-confirm v-if="canAdminEyeInWireless()" @click="removeCompetitorPage(index)"
                            variant="light" class="ml-2">
                            <i class="fas fa-trash" />
                        </e-button-confirm>
                    </b-list-group-item>
                </b-list-group>

                <div class="text-center mt-3">
                    <b-btn variant="light-primary" @click="addCompetitorPage">
                        <span v-tr>Add Competitor's Page|Ajouter page d'un compétiteur</span>
                        <i class="fas fa-plus ml-1" />
                    </b-btn>
                </div>
            </sidebar-group>

            <sidebar-group title="Ressources">
                <b-form-group :label="tr(`Page Brief|Document de brief`)">
                    <b-input-group>
                        <b-input-group-text><i class="fas fa-link-horizontal" /></b-input-group-text>
                        <e-input type="url" v-model="currentPage.meta.briefDocUrl"
                            :placeholder="tr(`URL of initial briefing document|URL du document de brief initial`)" />
                    </b-input-group>
                </b-form-group>
            </sidebar-group>
        </b-tab>
        <b-tab title="Page SEO">
            <sidebar-group title="Search Engine Visibility|Visibilités des moteurs de recherche"
                class="border-bottom-0">
                <b-form-group :label="'Preview Image|Image aperçu' | tr">
                    <input-image v-model="currentPage.meta.socialImage" size="xl" />
                </b-form-group>

                <e-translation-input v-model="currentPage.meta.title" @change="currentPage.syncSocial()"
                    label="SEO page Title|Titre de la page pour SEO" auto-translate :use-locales="cmsLocales">
                    <template #label-info>
                        <b-btn size="sm" variant="outline" @click="loadH1Title()">
                            <i class="fas fa-sync mr-1" />
                            <span v-tr>Load from H1|Charger depuis le H1</span>
                        </b-btn>
                    </template>
                </e-translation-input>

                <e-translation-input v-model="currentPage.meta.description" @change="currentPage.syncSocial()"
                    type="textarea" label="SEO page Description|Description de la page pour SEO" auto-translate
                    :use-locales="cmsLocales" />

                <b-form-group :label="'Search Engine Visibility|Visibility des moteurs de recherche' | tr">
                    <b-select v-model="currentPage.meta.searchVisibility">
                        <option value="visible" v-tr>Visible to search engines|Visible par les moteurs de recherche
                        </option>
                        <option value="hidden" v-tr>Hidden from search engines results|Caché des résultats des moteurs
                            de recherche</option>
                    </b-select>
                </b-form-group>
            </sidebar-group>

            <e-serp-snippet-simulator :title="currentPage.meta.title" :description="currentPage.meta.description"
                :permalink="currentPage.permalink" :base-url="cmsWebsite.domain" />

        </b-tab>
        <b-tab :title="'Soc. Media|Réseaux soc.' | tr" class="py-3 px-1">
            <h4 class="mb-4" v-tr>Social Media|Réseaux sociaux</h4>

            <e-translation-input v-model="currentPage.meta.socialTitle"
                label="Social page Title|Titre pour réseaux sociaux" :readonly="currentPage.meta.socialTitle.sameAsSEO"
                auto-translate :use-locales="cmsLocales" />
            <b-checkbox v-model="currentPage.meta.socialTitle.sameAsSEO"><span v-tr>Same as SEO page title|Identique au
                    titre de la page SEO</span></b-checkbox>
            <hr>
            <e-translation-input v-model="currentPage.meta.socialDescription" type="textarea"
                label="Social page Description|Description page pour réseaux sociaux" auto-translate
                :readonly="currentPage.meta.socialDescription.sameAsSEO" :use-locales="cmsLocales" />
            <b-checkbox v-model="currentPage.meta.socialDescription.sameAsSEO"><span v-tr>Same as SEO page
                    title|Identique au titre de la page SEO</span></b-checkbox>
        </b-tab>
        <b-tab :title="tr(`Translation|Traduction`)">
            <h4 class="mb-4" v-tr>Translation|Traduction</h4>

            <b-form-group>
                <e-translation-input v-model="currentPage.meta.translationContext" type="textarea"
                                     label="Translation context|Contexte de traduction" auto-translate :use-locales="cmsLocales">
                    <template #label-info>
                        <b-btn size="sm" variant="outline" @click="loadSEODesc()">
                            <i class="fas fa-sync mr-1" />
                            <span v-tr>Load from SEO description|Charger depuis la description SEO</span>
                        </b-btn>
                    </template>
                </e-translation-input>
            </b-form-group>
        </b-tab>
        <b-tab :title="'Advanced|Avancé' | tr" class="py-3 px-1">
            <sidebar-group title="Device Display|Affichage par appareil">
                <b-form-group>
                    <btn-checkbox v-model="currentPage.mediaQuery.desktop.visible"
                        :title="'Select if visible on desktop device|Visibilité sur ordinateur' | tr">
                        <i class="fas fa-desktop" style="font-size: 16pt;" />
                    </btn-checkbox>
                    <btn-checkbox v-model="currentPage.mediaQuery.tablet.visible"
                        :title="'Select if visible on tablet device|Visibilité sur tablette' | tr">
                        <i class="fas fa-tablet-screen" style="font-size: 16pt;" />
                    </btn-checkbox>
                    <btn-checkbox v-model="currentPage.mediaQuery.mobile.visible"
                        :title="'Select if visible on mobile device|Visibilité sur mobile' | tr">
                        <i class="fas fa-mobile-iphone" style="font-size: 16pt;" />
                    </btn-checkbox>
                    <btn-checkbox v-model="currentPage.mediaQuery.kiosk.visible"
                        :title="'Select if visible on kiosk device|Visibilité sur kiosque' | tr">
                        <i class="fak fa-kiosk" style="font-size: 16pt;" />
                    </btn-checkbox>
                </b-form-group>
                <div class="form-help mb-2" v-tr>You can enable/disable access to the page based on the user device|Vous
                    pouvez activer ou désactiver l'accès à la page en fonction de l'appareil utilisé</div>

                <b-form-group :label="tr('Fallback to|Rediriger vers')">
                    <input-link v-model="currentPage.fallbackLink" />
                </b-form-group>
            </sidebar-group>
<!--            <sidebar-group title="Eye-In Settings|Paramètres Eye-In" v-if="canAdminEyeInWireless()">-->
<!--                <b-form-group :label="tr(`Venue|Compte`)">-->
<!--                    <e-dealer-select v-model="currentPage.dealerid"/>-->
<!--                </b-form-group>-->
<!--            </sidebar-group>-->
        </b-tab>

        <b-modal v-model="categoryModalVisible" :title="'Create Category|Créer une catégorie' | tr"
            body-bg-variant="white" :ok-title="'Create|Créer' | tr" :ok-disabled="!isNewCategoryValid"
            @ok="createCategory">
            <e-translation-input v-model="newCategory.name" label="Category Name|Nom de la catégorie"
                @input="onCategoryNameChanged" auto-translate :use-locales="cmsLocales" />
            <b-form-group :label="'Key|Clé' | tr">
                <b-input v-model="newCategory.key" maxlength="60" placeholder="news, about-us" />
                <div class="form-help mt-2" v-tr>Lowercase, no spaces or special characters. Use '-' to separate
                    words|Minuscules, pas d'espaces ni de caractères spéciaux. Utilisez '-' pour séparer les mots</div>
            </b-form-group>
        </b-modal>

    </b-tabs>
</template>

<script>
import SidebarGroup from "@/components/editor/properties/components/sidebar-group.vue";
import BtnCheckbox from "@/components/editor/properties/inputs/btn-checkbox.vue";
import InputGlyph from "@/components/editor/properties/inputs/input-glyph.vue";
import InputImage from "@/components/editor/properties/inputs/input-image.vue";
import InputLink from "@/components/editor/properties/inputs/input-link.vue";
import EHint from "@vue-components/components/e-hint.vue";
import EInput from "@vue-components/components/e-input.vue";
import ETranslationInput from "@vue-components/components/e-translation-input.vue";
import CmsHelper from "@vue-components/helpers/CmsHelper.js";
import EStatusBadge from "@vue-components/components/e-status-badge.vue";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import Network from "@vue-components/helpers/Network.js";
import ESerpSnippetSimulator from "@vue-components/components/e-serp-snippet-simulator.vue";
import { extractHtmlText } from "@vue-components/helpers/utils.js";
import BadgeKeyword from "@components/editor/properties/seo/badge-keyword.vue";
import EButtonConfirm from "@vue-components/components/e-button-confirm.vue";
import InputKeywordSpecs from "@components/editor/properties/seo/input-keyword-specs.vue";

// page-settings-modal #hot-reload-debug
export default {
    name: `page-settings-modal`,
    components: {
        InputKeywordSpecs,
        EButtonConfirm, BadgeKeyword, EInput, EStatusBadge, InputLink, BtnCheckbox, SidebarGroup, EHint, InputGlyph, InputImage, ETranslationInput, ESerpSnippetSimulator
    },
    props: {
        value: { type: Object },
        tab: { type: Number, default: 0 },
    },
    data() {
        return {
            categories: [],
            showVariations: false,
            categoriesLoading: false,
            categoryModalVisible: false,
            newCategory: {
                name: {},
                key: ``
            }
        }
    },
    computed: {
        currentPage: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit(`input`, value);
            }
        },
        tabComputed: {
            get() {
                return this.tab;
            },
            set(value) {
                this.$emit(`update:tab`, value);
            }
        },
        isNewCategoryValid() {
            return (this.newCategory.key || ``).length > 0 && (this.$tr(this.newCategory.name) || ``).length > 0;
        }
    },
    created() {
        if (this.currentPage.type === `blog`) {
            this.loadBlogCategories();
        } else {
            this.loadCategories();
        }
        let specs = this.currentPage.specs;
        for (let lang of this.cmsWebsite.settings.languages) {
            if (!specs.words[lang]) {
                specs.words[lang] = { min: 0, max: 0 };
            }
            if (!specs.titles[lang]) {
                specs.titles[lang] = { min: 0, max: 0 };
            }
            if (!specs.paragraphs[lang]) {
                specs.paragraphs[lang] = { min: 0, max: 0 };
            }
            if (!specs.images[lang]) {
                specs.images[lang] = { min: 0, max: 0 };
            }
            if (!specs.images[lang]) {
                specs.images[lang] = { min: 0, max: 0 };
            }
            if (!specs.mainKeywords[lang]) {
                specs.mainKeywords[lang] = [];
            }
            if (!specs.keywords[lang]) {
                specs.keywords[lang] = [];
            }
        }
    },
    mounted() {

    },
    methods: {
        async loadCategories() {
            try {
                this.categoriesLoading = true;
                const resp = await NetworkV2.get(`/api/cms/categories/${this.dealerid}`);
                this.categories = resp.data || [];
            } catch (e) {
                console.error(e);
                this.categories = [];
            }
            this.categoriesLoading = false;
        },
        async loadBlogCategories() {
            try {
                this.categoriesLoading = true;
                const resp = await Network.get(`/blog/admin/categories`, null, {system_api: true});
                this.categories = resp.data || [];
            } catch (e) {
                console.error(e);
                this.categories = [];
            }
            this.categoriesLoading = false;
        },
        openCategoryModal() {
            this.newCategory = { name: {}, key: `` };
            this.categoryModalVisible = true;
        },
        onCategoryNameChanged() {
            if (!this.newCategory.key) {
                const name = this.$tr(this.newCategory.name) || ``;
                this.newCategory.key = CmsHelper.textToPermalink(name);
            }
        },
        async createCategory() {
            try {
                const payload = {
                    category: {
                        dealerid: this.dealerid,
                        key: this.newCategory.key,
                        name: this.newCategory.name
                    }
                };
                const resp = await NetworkV2.post(`/api/cms/categories/create`, payload);
                const created = resp.data;
                this.categories.push(created);
                this.currentPage.category = created.key;
                this.categoryModalVisible = false;
            } catch (e) {
                console.error(e);
                this.$root.showErrorMessage && this.$root.showErrorMessage(`Error`, `Couldn't create category`);
            }
        },
        onCurrentpageTypeChange(type) {
            if (type === `blog`) {
                this.loadBlogCategories();
            }
        },
        updatePermalink() {
            for (let lang in this.newPage?.name || this.currentPage.name) {
                this.$set(this.currentPage.permalink, lang, CmsHelper.textToPermalink(this.currentPage.name[lang]));
            }
        },
        loadH1Title() {
            const components = this.cmsPage.getComponents(`text`, true, true);
            for (let component of components) {
                if (component.preset === `h1`) {
                    for (let lang of this.cmsWebsite.settings.languages) {
                        this.$set(this.currentPage.meta.title, lang, extractHtmlText(component.text[lang]));
                    }
                }
            }
            this.currentPage.syncSocial();
        },
        loadSEODesc() {
            this.currentPage.meta.translationContext = this.currentPage.meta.description;
        },
        // AI Part
        getAIPayload() {
            return {
                topic: this.currentPage.hiddenMeta.topic[this.displayLanguage],
                mainKeywords: this.currentPage.hiddenMeta.mainKeywords[this.displayLanguage],
                keywords: this.currentPage.hiddenMeta.keywords[this.displayLanguage],
                audience: this.currentPage.hiddenMeta.audience[this.displayLanguage],
                targetMarket: this.currentPage.hiddenMeta.targetMarket[this.displayLanguage],
                competitors: this.currentPage.hiddenMeta.competitors[this.displayLanguage],
                minWords: this.currentPage.specs.words[this.displayLanguage]?.min,
                maxWords: this.currentPage.specs.words[this.displayLanguage]?.max,
                language: this.displayLanguage
            }
        },
        async generateAIKeywords() {
            try {
                this.showLoading();
                const resp = await NetworkV2.post(`/api/seo/assistant/page/keywords`, this.getAIPayload());
                this.$set(this.cmsPage.ai.keywords, this.displayLanguage, resp.data);

                const { mainKeywords, keywords } = resp.data;

                this.$set(this.cmsPage.specs.mainKeywords, this.displayLanguage, mainKeywords);
                this.$set(this.cmsPage.specs.keywords, this.displayLanguage, keywords);

                let pageMainKeywords = this.currentPage.hiddenMeta.mainKeywords[this.displayLanguage];
                if (!pageMainKeywords) {
                    pageMainKeywords = [];
                }
                for (let keywordSpec of mainKeywords) {
                    if (pageMainKeywords.indexOf(keywordSpec.keyword) === -1) {
                        pageMainKeywords.push(keywordSpec.keyword);
                    }
                }
                this.$set(this.currentPage.hiddenMeta.mainKeywords, this.displayLanguage, pageMainKeywords);

                let pageKeywords = this.currentPage.hiddenMeta.keywords[this.displayLanguage];
                if (!pageKeywords) {
                    pageKeywords = [];
                }
                for (let keywordSpec of keywords) {
                    if (pageKeywords.indexOf(keywordSpec.keyword) === -1) {
                        pageKeywords.push(keywordSpec.keyword);
                    }
                }
                this.$set(this.currentPage.hiddenMeta.keywords, this.displayLanguage, pageKeywords);
                this.hideLoading();
            } catch (e) {
                console.error(e);
                this.showErrorFeedback(e);
            }
        },
        async generateAIPageSpecs() {
            try {
                this.showLoading();
                const resp = await NetworkV2.post(`/api/seo/assistant/page/specs`, this.getAIPayload());
                this.$set(this.cmsPage.ai.specs, this.displayLanguage, resp.data);

                this.$set(this.cmsPage.specs.words, this.displayLanguage, resp.data.words);
                this.$set(this.cmsPage.specs.titles, this.displayLanguage, resp.data.titles);
                this.$set(this.cmsPage.specs.paragraphs, this.displayLanguage, resp.data.paragraphs);
                this.$set(this.cmsPage.specs.images, this.displayLanguage, resp.data.images);

                // for (let key in resp.data) {
                //     this.cmsPage.specs[key][this.displayLanguage] = resp.data[key];
                // }
                if (!this.currentPage.meta.title[this.displayLanguage])
                    this.$set(this.currentPage.meta.title, this.displayLanguage, resp.data.seoTitle);
                if (!this.currentPage.meta.description[this.displayLanguage])
                    this.$set(this.currentPage.meta.description, this.displayLanguage, resp.data.seoDescription);
                this.hideLoading();
            } catch (e) {
                console.error(e);
                this.showErrorFeedback(e);
            }
        },
        async generateAIPageMeta() {
            try {
                this.showLoading();
                const resp = await NetworkV2.post(`/api/seo/assistant/page/meta`, this.getAIPayload());
                this.$set(this.cmsPage.ai.meta, this.displayLanguage, resp.data);

                if (!this.currentPage.meta.title[this.displayLanguage])
                    this.$set(this.currentPage.meta.title, this.displayLanguage, resp.data.title);
                if (!this.currentPage.meta.description[this.displayLanguage])
                    this.$set(this.currentPage.meta.description, this.displayLanguage, resp.data.description);
                this.hideLoading();
            } catch (e) {
                console.error(e);
                this.showErrorFeedback(e);
            }
        },
        applySeoTitle() {
            this.$set(this.currentPage.meta.title, this.displayLanguage, this.cmsPage.ai.meta[this.displayLanguage].title);
        },
        applySeoDescription() {
            this.$set(this.currentPage.meta.description, this.displayLanguage, this.cmsPage.ai.meta[this.displayLanguage].description);
        },
        /*
        COMPETITORS
         */
        addCompetitorPage() {
            if (!this.currentPage.hiddenMeta.competitors[this.displayLanguage]) {
                this.$set(this.currentPage.hiddenMeta.competitors, this.displayLanguage, []);
            }
            this.currentPage.hiddenMeta.competitors[this.displayLanguage].push(`https://`);
        },
        removeCompetitorPage(index) {
            this.currentPage.hiddenMeta.competitors[this.displayLanguage].splice(index, 1);
        },
    }
}
</script>

<style lang="scss" scoped>
</style>
